import { Link } from "@inertiajs/react"
import { HeartCrack, Swords, TrendingUp } from "lucide-react"

import { RaceShield } from "@/components/race-shield"
import { SegmentHeader } from "@/components/segment-header"
import { TeamName } from "@/components/team-name"
import { Card, CardContent } from "@/components/ui/card"
import { VsDiamond } from "@/components/vs-diamond"
import { formatGold, positionClass } from "@/lib/format"
import { getRaceDisplayName, getTeamInitials } from "@/lib/race-colors"
import { cn } from "@/lib/utils"
import { leagueMatchPath, leagueTeamPath, leagueTeamPlayerPath } from "@/routes"
import type { LeagueHubProps } from "@/types"
import type { StandingRow } from "@/types/standings"

interface TeamDashboardProps {
  leagueId: string
  userTeam: NonNullable<LeagueHubProps["user_team"]>
  upcomingMatch: LeagueHubProps["upcoming_match"]
  rosterAlerts: LeagueHubProps["roster_alerts"]
  standings: StandingRow[]
}

function StatCell({
  value,
  label,
  className,
}: {
  value: string
  label: string
  className?: string
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        className={cn(
          "font-display text-accent text-xl leading-none font-bold",
          className,
        )}
      >
        {value}
      </span>
      <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
        {label}
      </span>
    </div>
  )
}

interface ActionItemProps {
  icon: React.ReactNode
  label: string
  detail: string
  href: string
  color: string
}

function ActionItem({ icon, label, detail, href, color }: ActionItemProps) {
  return (
    <Link
      href={href}
      className="group/action flex items-center gap-3 py-2 pr-3 transition-colors hover:bg-[oklch(0.22_0.01_55)]"
    >
      <span className="flex-shrink-0" style={{ color: color }}>
        {icon}
      </span>
      <span className="text-muted-foreground text-sm">
        <span className="font-semibold text-[var(--foreground)]">{label}</span>
        {" — "}
        {detail}
      </span>
    </Link>
  )
}

export function TeamDashboard({
  leagueId,
  userTeam,
  upcomingMatch,
  rosterAlerts,
  standings,
}: TeamDashboardProps) {
  const userRow = standings.find((r) => r.team.id === userTeam.id)
  const rank = userRow?.rank ?? 0
  const wins = userRow?.wins ?? 0
  const draws = userRow?.draws ?? 0
  const losses = userRow?.losses ?? 0
  const points = userRow?.points ?? 0

  const actions: ActionItemProps[] = []

  if (upcomingMatch && !upcomingMatch.lineup_submitted) {
    actions.push({
      icon: <Swords className="size-4" />,
      label: "Set Lineup",
      detail: `Lineup not submitted for Round ${upcomingMatch.round_number}`,
      href: leagueMatchPath(leagueId, upcomingMatch.id),
      color: "oklch(0.80 0.16 85)",
    })
  }

  for (const alert of rosterAlerts) {
    if (alert.type === "injury") {
      actions.push({
        icon: <HeartCrack className="size-4" />,
        label: alert.player_name,
        detail: alert.detail,
        href: leagueTeamPath(leagueId, userTeam.id),
        color: "oklch(0.65 0.20 28)",
      })
    } else if (alert.type === "level_up") {
      actions.push({
        icon: <TrendingUp className="size-4" />,
        label: alert.player_name,
        detail: alert.detail,
        href: leagueTeamPlayerPath(leagueId, userTeam.id, alert.player_id),
        color: "oklch(0.65 0.20 145)",
      })
    }
  }

  return (
    <>
      <Card className="gap-0 py-0">
        <CardContent className="px-6 pt-5 pb-4">
          {/* Scoreboard layout: Your team (left) — VS — Opponent (right) */}
          <div className="flex items-start gap-4">
            {/* Left: Team identity + stats */}
            <Link
              href={leagueTeamPath(leagueId, userTeam.id)}
              className="flex-shrink-0"
            >
              <RaceShield
                race={userTeam.race}
                hue={userTeam.color_hue ?? undefined}
                label={getTeamInitials(userTeam.name)}
                size="lg"
              />
            </Link>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div>
                <Link
                  href={leagueTeamPath(leagueId, userTeam.id)}
                  className="inline-block"
                >
                  <h2 className="font-display text-3xl leading-none font-bold">
                    <TeamName
                      name={userTeam.name}
                      hue={userTeam.color_hue}
                      isUser
                    />
                  </h2>
                </Link>
                <p className="text-muted-foreground mt-0.5 text-xs font-semibold tracking-wide uppercase">
                  {getRaceDisplayName(userTeam.race)}
                </p>
              </div>

              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                {rank > 0 && (
                  <>
                    <StatCell
                      value={`#${rank}`}
                      label="Standing"
                      className={positionClass(rank)}
                    />
                    <span className="text-border-heavy">·</span>
                  </>
                )}
                <StatCell
                  value={String(userTeam.player_count)}
                  label="Players"
                />
                <span className="text-border-heavy">·</span>
                <StatCell
                  value={formatGold(userTeam.treasury_balance)}
                  label="Treasury"
                />
                <span className="text-border-heavy">·</span>
                <StatCell value={formatGold(userTeam.team_value)} label="TV" />
              </div>

              <div className="flex items-baseline gap-2 text-xs">
                <span
                  className={cn(
                    "font-display text-lg leading-none font-bold",
                    points > 0 && "text-[var(--accent)]",
                  )}
                >
                  {points}
                  <span className="text-muted-foreground ml-0.5 text-[10px] font-semibold tracking-wide uppercase">
                    pts
                  </span>
                </span>
                <span className="text-muted-foreground">
                  {wins}W {draws}D {losses}L
                </span>
              </div>
            </div>

            {/* Right: Next match opponent — desktop */}
            {upcomingMatch && (
              <Link
                href={leagueMatchPath(leagueId, upcomingMatch.id)}
                className="hidden items-center gap-4 md:flex"
              >
                <div className="flex flex-col items-end gap-0.5">
                  <span
                    className="font-display text-[10px] font-bold tracking-widest uppercase"
                    style={{ color: "var(--accent-dim)" }}
                  >
                    Next Match
                  </span>
                  <span
                    className="font-display text-lg leading-none font-bold"
                    style={{ color: "var(--accent)" }}
                  >
                    Round {upcomingMatch.round_number}
                  </span>
                </div>
                <VsDiamond />
                <RaceShield
                  race={upcomingMatch.opponent_race}
                  hue={upcomingMatch.opponent_color_hue ?? undefined}
                  label={getTeamInitials(upcomingMatch.opponent_name)}
                  size="sm"
                />
                <div className="flex flex-col gap-0.5">
                  <span className="font-display text-lg leading-none font-bold">
                    <TeamName
                      name={upcomingMatch.opponent_name}
                      hue={upcomingMatch.opponent_color_hue}
                    />
                  </span>
                  <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                    {getRaceDisplayName(upcomingMatch.opponent_race)} · TV{" "}
                    {formatGold(upcomingMatch.opponent_tv)}
                  </span>
                </div>
              </Link>
            )}
          </div>

          {/* Mobile: next match row */}
          {upcomingMatch && (
            <Link
              href={leagueMatchPath(leagueId, upcomingMatch.id)}
              className="mt-5 flex items-center gap-3 border-t border-[var(--border)] pt-5 md:hidden"
            >
              <VsDiamond />
              <RaceShield
                race={upcomingMatch.opponent_race}
                hue={upcomingMatch.opponent_color_hue ?? undefined}
                label={getTeamInitials(upcomingMatch.opponent_name)}
                size="md"
                className="flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <span
                  className="font-display block text-[10px] font-bold tracking-widest uppercase"
                  style={{ color: "var(--accent-dim)" }}
                >
                  Next Match · Round {upcomingMatch.round_number}
                </span>
                <span className="font-display block text-lg leading-none font-bold">
                  <TeamName
                    name={upcomingMatch.opponent_name}
                    hue={upcomingMatch.opponent_color_hue}
                  />
                </span>
                <span className="text-muted-foreground mt-0.5 block text-[10px] font-semibold tracking-wide uppercase">
                  {getRaceDisplayName(upcomingMatch.opponent_race)} · TV{" "}
                  {formatGold(upcomingMatch.opponent_tv)}
                </span>
              </div>
            </Link>
          )}
        </CardContent>
      </Card>

      {/* Required Actions — separate card */}
      {actions.length > 0 && (
        <div className="mt-10">
          <Card className="gap-0 py-0">
            <CardContent className="px-6 pt-4 pb-3">
              <SegmentHeader
                rules={false}
                as="span"
                className="mb-2 block text-[10px] tracking-[0.2em]"
                label="Requires Attention"
              />
              <div className="space-y-0.5">
                {actions.map((action, i) => (
                  <ActionItem key={i} {...action} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}
